// src/pages/CalculateLabour.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function CalculateLabour() {
  const { projectId } = useParams();

  const [labourData, setLabourData] = useState([]);
  const [labourExtraPercent, setLabourExtraPercent] = useState(0);
  const [rmData, setRmData] = useState([]);
  const [projectYears, setProjectYears] = useState([]);
  const [capUtil, setCapUtil] = useState([]);
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Parse "mm-yyyy" into { month, year }
  const prod = useMemo(() => {
    const s = termLoanInput.startMonth || "";
    const [m, y] = s.split("-").map((t) => parseInt(t, 10));
    if (!m || !y) return null;
    return { month: m, year: y };
  }, [termLoanInput.startMonth]);

  // Months active in a financial year "YYYY-YY"
  const getMonthsActive = (fyLabel) => {
    if (!prod) return 12;
    const fyStart = parseInt(fyLabel.split("-")[0], 10);
    if (isNaN(fyStart)) return 12;
    const fyEnd = fyStart + 1;
    const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
    return Math.max(0, Math.min(raw, 12));
  };

  // Load data from API
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setLabourData(data.labourCost || []);
        setLabourExtraPercent(parseFloat(data.labourExtraPercent) || 0);
        setRmData(data.rmConsumptionInputs || []);
        setProjectYears(data.projectYears || []);
        setCapUtil(data.capacityUtilizationPerYear || []);
        setTermLoanInput(data.termLoanInput || { startMonth: "" });
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load Labour data.");
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  // Installed capacity per product (MT)
  const installedArr = useMemo(
    () =>
      rmData.map((r) => {
        const days = parseFloat(r.workingDays) || 0;
        const hrs = parseFloat(r.workingHoursPerDay) || 0;
        const out = parseFloat(r.outputPerHour) || 0;
        return (days * hrs * out) / 1000;
      }),
    [rmData]
  );

  // Total annual production
  const totalAnnualProduction = useMemo(
    () => installedArr.reduce((sum, v) => sum + v, 0),
    [installedArr]
  );

  // Year-wise output (MT)
  const yearWiseOutput = useMemo(
    () =>
      projectYears.map((fy, i) => {
        const u = parseFloat(capUtil[i]) || 0;
        const months = getMonthsActive(fy);
        return installedArr
          .map((inst) => (inst * u * months) / (100 * 12))
          .reduce((s, v) => s + v, 0);
      }),
    [projectYears, capUtil, installedArr, prod]
  );

  // Helpers for labour cost
  const calculateAmount = (nos, rate) => {
    const n = parseFloat(nos) || 0;
    const r = parseFloat(rate) || 0;
    return n * r * 12;
  };

  const totalAmount = useMemo(
    () =>
      labourData.reduce(
        (sum, row) => sum + calculateAmount(row.nos, row.ratePerMonth),
        0
      ),
    [labourData]
  );

  const totalExtras = useMemo(
    () => (totalAmount * labourExtraPercent) / 100,
    [totalAmount, labourExtraPercent]
  );

  const totalLabourCost = useMemo(
    () => totalAmount + totalExtras,
    [totalAmount, totalExtras]
  );

  const costPerMT = useMemo(
    () =>
      totalAnnualProduction > 0 ? totalLabourCost / totalAnnualProduction : 0,
    [totalLabourCost, totalAnnualProduction]
  );

  // Year-wise labour cost in lakhs
  const labourCostsList = useMemo(
    () =>
      yearWiseOutput.map((prod) =>
        parseFloat(((prod * costPerMT) / 100000).toFixed(2))
      ),
    [yearWiseOutput, costPerMT]
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center w-full">
        <svg
          className="animate-spin h-10 w-10 text-gray-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-center py-8 w-full">{error}</div>;
  }

  const format = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "";

  return (
    <div className="bg-white">
      {/* Summary */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Summary</h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                  Type of Work
                </th>
                <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                  Manpower Req
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Nos
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Rate/Month
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Amount (Annual)
                </th>
              </tr>
            </thead>
            <tbody>
              {labourData.map((row, idx) => (
                <tr key={idx} className="even:bg-gray-50">
                  <td className="px-2 py-1 border border-gray-200">
                    {row.typeOfWork}
                  </td>
                  <td className="px-2 py-1 border border-gray-200">
                    {row.manpower}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {row.nos}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {row.ratePerMonth}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {format(calculateAmount(row.nos, row.ratePerMonth))}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td
                  colSpan={4}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  Total
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  Rs. {format(totalAmount)}
                </td>
              </tr>
              <tr className="font-medium">
                <td
                  colSpan={4}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  Extras PF (%)
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {format(labourExtraPercent)}%
                </td>
              </tr>
              <tr className="bg-gray-200 font-bold">
                <td
                  colSpan={4}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  Total Extras (PF)
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  Rs. {format(totalExtras)}
                </td>
              </tr>
              <tr className="font-medium">
                <td
                  colSpan={4}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  Total Labour Cost @100%
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  Rs. {format(totalLabourCost)}
                </td>
              </tr>
              <tr className="bg-gray-200 font-bold">
                <td
                  colSpan={4}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  Total Annual Production
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {format(totalAnnualProduction)}
                </td>
              </tr>
              <tr className="font-medium">
                <td
                  colSpan={4}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  Cost per MT
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  Rs. {format(costPerMT)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Year-wise Labour Cost */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Year-wise Labour Cost</h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                  Year
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Production (MT)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Cost/MT (Rs)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Total Cost (Lakhs)
                </th>
              </tr>
            </thead>
            <tbody>
              {projectYears.map((yr, idx) => (
                <tr key={idx} className="even:bg-gray-50">
                  <td className="px-2 py-1 border border-gray-200">{yr}</td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {format(yearWiseOutput[idx])}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {format(costPerMT)}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {format(labourCostsList[idx])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
